import { Controller } from '@hotwired/stimulus';
import {
  continueToNextSection,
  currentSection,
  updateDocumentUserState,
  patchDocumentUserState,
  showAiErrorModal,
  handleAiAssistantFallback,
  AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT,
  registerErrorInRollbar,
  getCsrfToken,
  normalizeNodeUid,
  getDusFromDom,
  updateDusInDom,
  vpeChatErrorBlocksFeedback,
} from '../../utils/case_experience/case_experience_in_rails';
import {
  closeRailsuiModal,
  removeDynamicVpeErrorModals,
  openVpeFallbackErrorModal,
  waitForEmbeddedVpeErrorModalReady,
  isRailsuiModalOpen,
  vpeErrorModalController,
} from '../../utils/case_experience/vpe_fallback_error_modal';
import {
  clearVpePersistedChatError,
  hasVpePersistedChatError,
  markVpePersistedChatError,
  resetVpePersistedChatErrorSessionStateForTests,
} from '../../utils/case_experience/vpe_chat_error_persistence';
import {
  isUserMessage,
  isAssistantMessage,
  msgContent,
  isAssistantError,
  isAssistantDone,
  lastMessage,
  checkConsecutiveAssistantError,
  submitChatOnEnter,
} from '../../utils/vpe_chat_message_utils';

const MAX_RETRIES = 2;
const RESPONSE_SAFETY_TIMEOUT_MS = 30000;
const MOBILE_VIEWPORT_MEDIA_QUERY = '(max-width: 767px)';

export { resetVpePersistedChatErrorSessionStateForTests };

const awaitingTurnCache = new Map();
const pendingFocusCache = new Map();
const vpeFatalErrorModalOpenedForDocUser = new Set();

export const resetVpeFatalErrorModalSessionStateForTests = () => {
  vpeFatalErrorModalOpenedForDocUser.clear();
};

const vpeAiFallbackCardsAppliedInDus = dus => Boolean(dus?.root?.aiFallbackCards?.value);
const vpeFatalErrorModalLatchKey = (documentId, userId) => `${documentId}:${userId}`;
const dispatchVpeFeedbackCardMayGenerate = (documentId, chatNodeUid) =>
  document.dispatchEvent(new CustomEvent('vpe-feedback-card-may-generate', {
    bubbles: true,
    detail: { documentId, chatUid: normalizeNodeUid(chatNodeUid) },
  }));

// Utility for DOM element enable/disable/placeholder
function setInputState(ctrl, disabled, placeholder = 'Ask me anything') {
  if (ctrl.hasInputTarget) {
    ctrl.inputTarget.disabled = disabled;
    ctrl.inputTarget.placeholder = placeholder;
  }
}

function clearResponseSafetyTimer(ctrl) {
  clearTimeout(ctrl._responseSafetyTimeout);
  ctrl._responseSafetyTimeout = null;
}

function clearResponseSafetyWatch(ctrl) {
  clearResponseSafetyTimer(ctrl);
  ctrl._responseSafetyDeadline = null;
}

function getThreadIdFromForm(element) {
  const form = element.querySelector('form[action*="/ai_assistant/threads/"]');
  const action = form?.action;
  return action ? (action.match(/\/ai_assistant\/threads\/(\d+)\/messages/) || [])[1] || null : null;
}

function getThreadIdFromMessagesMount(ctrl) {
  if (!ctrl.hasMessagesContainerTarget) return null;
  const el = ctrl.messagesContainerTarget.querySelector('[id^="thread_messages_"]');
  return el?.id?.match(/^thread_messages_(\d+)$/)?.[1] || null;
}

function resolvedThreadId(ctrl) {
  return getThreadIdFromForm(ctrl.element)
    || getThreadIdFromMessagesMount(ctrl)
    || (ctrl.hasThreadIdValue && ctrl.threadIdValue ? String(ctrl.threadIdValue) : null);
}

function makeAwaitingCacheKey(ctrl) {
  const threadId = resolvedThreadId(ctrl);
  return threadId && ctrl.nodeUidValue ? `${normalizeNodeUid(ctrl.nodeUidValue)}::${threadId}` : null;
}

function debounceSync(ctrl, fn, delay = 150) {
  clearTimeout(ctrl._syncDebounce);
  ctrl._syncDebounce = setTimeout(fn, delay);
}

export default class extends Controller {
  static targets = [
    'input',
    'messagesContainer',
    'sendButton',
    'feedbackButton',
    'noMoreQuestionsCheckbox',
    'feedbackModal',
    'feedbackModalHost',
    'errorModal',
  ];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    assistantId: String,
    versionId: Number,
    threadId: String,
    messages: Array,
    feedbackSubmitted: Boolean,
    vtvEnabled: Boolean,
    voiceAgentId: String,
  };

  /** Turbo fetch failures + assistant-side failures (e.g. OpenAI error message) before fatal modal; aligns with React errorCount >= 2 */
  errorCount = 0;
  /** True while the VPE chat error overlay is shown (avoids stacking modals / duplicate work) */
  errorModalOpen = false;
  /** Dedupe counting when finishTurnIfReady runs multiple times for same assistant-error turn (messagesValue length key) */
  _lastCountedAssistantErrorKey = null;
  /** Debounce timer for sync after messages DOM mutations */
  _syncDebounce = null;
  /** Fires restoreFormAfterError + error modal if still awaiting after RESPONSE_SAFETY_TIMEOUT_MS */
  _responseSafetyTimeout = null;
  /** Wall-clock deadline for the current turn; not extended by sync/stream reschedules */
  _responseSafetyDeadline = null;
  /**
   * True from turbo:submit-start until assistant message is done or we error out.
   * false: user can type; true: input should be disabled + “AI is responding” placeholder.
   */
  _awaitingAssistantResponse = false;
  /** One-shot refetch after connect if thread looks stuck (last msg user-only or empty assistant) */
  _connectRecoveryRefetchScheduled = false;
  /** Delayed handler id for scheduleConnectStuckTurnRecoveryIfNeeded */
  _connectRecoveryTimeoutId = null;
  /** When true, focus next connected input after turn-complete re-render */
  _pendingFocusAfterTurn = false;

  connect() {
    // Setup debounced listeners once
    this._updateSendButtonState = this.updateSendButtonState.bind(this);
    this._onMessagesDomMutated = this.debouncedSyncAfterMessagesDomChange.bind(this);
    this._onAfterTurboStreamRender = this.afterTurboStreamRender.bind(this);
    document.addEventListener('turbo:after-stream-render', this._onAfterTurboStreamRender);

    // Setup mutation observer only if messages container exists
    if (this.hasMessagesContainerTarget) {
      this._mutationObserver = new MutationObserver(this._onMessagesDomMutated);
      this._mutationObserver.observe(this.messagesContainerTarget, { childList: true, subtree: true });
    }
    if (this.hasInputTarget)
      this.inputTarget.addEventListener('input', this._updateSendButtonState);

    this._onViewportChange = () => this.updateFeedbackButtonState();
    this._mobileViewportQuery = window.matchMedia(MOBILE_VIEWPORT_MEDIA_QUERY);
    this._mobileViewportQuery.addEventListener('change', this._onViewportChange);

    // Restore "awaiting" UI from cache if needed
    const cacheKey = makeAwaitingCacheKey(this);
    const cached = cacheKey && awaitingTurnCache.get(cacheKey);
    if (cached) {
      const deadline = cached.deadline
        ?? (cached.ts != null ? cached.ts + RESPONSE_SAFETY_TIMEOUT_MS : null);
      if (deadline && deadline > Date.now()) {
        this._awaitingAssistantResponse = true;
        this._responseSafetyDeadline = deadline;
        this.scheduleResponseSafetyTimeout('connect-cache');
      }
    }

    this.applyAwaitingAssistantInputUi();
    this.updateSendButtonState();
    if (this.feedbackSubmittedValue) {
      setInputState(this, true);
      if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = true;
    }
    this.messagesValueChanged();

    void this.maybeRestorePersistedFatalErrorModal();

    if (getThreadIdFromForm(this.element) || (this.hasThreadIdValue && this.threadIdValue)) {
      void this.syncMessagesToDus().then(ok => {
        if (ok) {
          this.finishTurnIfReady();
          this.scheduleConnectStuckTurnRecoveryIfNeeded();
        }
        void this.maybeRestorePersistedFatalErrorModal();
      });
    }
  }

  errorModalTargetConnected() {
    void this.maybeRestorePersistedFatalErrorModal();
  }

  disconnect() {
    this._mutationObserver?.disconnect();
    this._mutationObserver = null;

    if (this._awaitingAssistantResponse && !this.feedbackSubmittedValue) {
      const cacheKey = makeAwaitingCacheKey(this);
      if (cacheKey) {
        awaitingTurnCache.set(cacheKey, {
          deadline: this._responseSafetyDeadline ?? (Date.now() + RESPONSE_SAFETY_TIMEOUT_MS),
        });
      }
    }
    this._awaitingAssistantResponse = false;
    clearTimeout(this._syncDebounce);
    clearTimeout(this._connectRecoveryTimeoutId);
    this._connectRecoveryTimeoutId = null;
    this._connectRecoveryRefetchScheduled = false;
    this._lastCountedAssistantErrorKey = null;
    clearResponseSafetyWatch(this);

    if (this._updateSendButtonState && this.hasInputTarget)
      this.inputTarget.removeEventListener('input', this._updateSendButtonState);

    this._mobileViewportQuery?.removeEventListener('change', this._onViewportChange);

    if (this._onAfterTurboStreamRender)
      document.removeEventListener('turbo:after-stream-render', this._onAfterTurboStreamRender);

    this._removeVpeFeedbackModalListeners();
  }

  _ensureVpeFeedbackModalListeners() {
    if (this._vpeFeedbackModalListenersBound) return;
    this._boundOnVpeFeedbackSubmitted = this.handleVpeFeedbackModalSubmitted.bind(this);
    this._boundOnVpeFeedbackClose = this.closeFeedbackModalHost.bind(this);
    this.element.addEventListener('vpe-feedback-modal:submitted', this._boundOnVpeFeedbackSubmitted);
    this.element.addEventListener('vpe-feedback-modal:close', this._boundOnVpeFeedbackClose);
    this._vpeFeedbackModalListenersBound = true;
  }
  _removeVpeFeedbackModalListeners() {
    if (!this._vpeFeedbackModalListenersBound) return;
    this.element.removeEventListener('vpe-feedback-modal:submitted', this._boundOnVpeFeedbackSubmitted);
    this.element.removeEventListener('vpe-feedback-modal:close', this._boundOnVpeFeedbackClose);
    this._vpeFeedbackModalListenersBound = false;
  }

  buildVpeFeedbackModalUrl(threadId, context) {
    const params = new URLSearchParams({
      thread_id: String(threadId),
      context,
      node_uid: this.nodeUidValue,
    });
    return `/documents/${this.documentIdValue}/case_experience/vpe_feedback_modal?${params}`;
  }

  closeFeedbackModalHost() {
    if (this.hasFeedbackModalHostTarget) this.feedbackModalHostTarget.style.display = 'none';
    if (this.hasFeedbackModalTarget) {
      this.feedbackModalTarget.removeAttribute('src');
      this.feedbackModalTarget.innerHTML = '';
    }
    document.body.style.overflow = '';
  }

  feedbackModalFrameLoaded = event => {
    if (!this.hasFeedbackModalTarget || event.target !== this.feedbackModalTarget) return;
    if (!this.feedbackModalTarget.innerHTML.trim()) {
      this.closeFeedbackModalHost();
      this.feedbackSubmittedValue = true;
    }
  };
  feedbackModalFetchError = event => {
    if (!this.hasFeedbackModalTarget || event.target !== this.feedbackModalTarget) return;
    if (event.detail?.response?.status === 204) {
      this.closeFeedbackModalHost();
      this.feedbackSubmittedValue = true;
      return;
    }
    this.closeFeedbackModalHost();
  };

  handleVpeFeedbackModalSubmitted = async () => {
    this.closeFeedbackModalHost();
    if (this.feedbackSubmittedValue) return;
    const tid = resolvedThreadId(this);
    if (!tid) return;
    this.feedbackSubmittedValue = true;
    if (this.hasFeedbackButtonTarget) this.feedbackButtonTarget.disabled = true;
    if (this.hasNoMoreQuestionsCheckboxTarget) {
      this.noMoreQuestionsCheckboxTarget.disabled = true;
    }
    setInputState(this, true);
    if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = true;
    if (!vpeChatErrorBlocksFeedback(getDusFromDom()))
      dispatchVpeFeedbackCardMayGenerate(this.documentIdValue, this.nodeUidValue);
    await this.continueToNextSection();
  };

  afterTurboStreamRender = () => {
    if (this.feedbackSubmittedValue || !this._awaitingAssistantResponse) return;
    if (!resolvedThreadId(this)) return;
    void this.syncMessagesToDus().then(ok => {
      if (ok) this.finishTurnIfReady();
    });
  };

  updateSendButtonState = () => {
    if (!this.hasSendButtonTarget) return;
    const inputEmpty = !this.hasInputTarget || this.inputTarget.value.trim() === '';
    this.sendButtonTarget.disabled =
      inputEmpty
      || this.feedbackSubmittedValue
      || this.inputTarget?.disabled === true
      || this._awaitingAssistantResponse;
  };

  submitOnEnter = (event) => {
    submitChatOnEnter(event, {
      inputTarget: this.hasInputTarget ? this.inputTarget : null,
      sendButtonTarget: this.hasSendButtonTarget ? this.sendButtonTarget : null,
    });
  };

  applyAwaitingAssistantInputUi() {
    if (!this.hasInputTarget || this.feedbackSubmittedValue) return;
    if (this._awaitingAssistantResponse) {
      setInputState(this, true, 'AI is responding...');
    } else {
      setInputState(this, false, 'Ask me anything');
    }
  }

  inputTargetConnected() {
    this.applyAwaitingAssistantInputUi();
    this.updateSendButtonState();

    const cacheKey = makeAwaitingCacheKey(this);
    const cachedPendingFocus = cacheKey ? pendingFocusCache.get(cacheKey) === true : false;
    if (cachedPendingFocus && !this._awaitingAssistantResponse)
      this._pendingFocusAfterTurn = true;
    if (this._pendingFocusAfterTurn && !this._awaitingAssistantResponse) {
      this._pendingFocusAfterTurn = false;
      if (cacheKey) pendingFocusCache.delete(cacheKey);
      requestAnimationFrame(() => { this.inputTarget?.focus(); });
    }
  }

  sendButtonTargetConnected() { this.updateSendButtonState(); }

  messagesValueChanged = () => {
    this.updateFeedbackButtonState();
  };

  getUserMessageCount = () =>
    Array.isArray(this.messagesValue) ? this.messagesValue.filter(isUserMessage).length : 0;

  meetsMinimumMessageCount = () =>
    this.getUserMessageCount() >= AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT;

  isMobileViewport = () => window.matchMedia(MOBILE_VIEWPORT_MEDIA_QUERY).matches;

  mobileFeedbackAcknowledged = () => {
    if (!this.hasNoMoreQuestionsCheckboxTarget || !this.isMobileViewport()) return true;
    return this.noMoreQuestionsCheckboxTarget.checked;
  };

  shouldEnableFeedbackButton = () =>
    this.meetsMinimumMessageCount() && this.mobileFeedbackAcknowledged();

  updateFeedbackButtonState = () => {
    if (this.hasNoMoreQuestionsCheckboxTarget && !this.meetsMinimumMessageCount()) {
      this.noMoreQuestionsCheckboxTarget.checked = false;
    }
    if (this.hasFeedbackButtonTarget) {
      this.feedbackButtonTarget.disabled =
        !this.shouldEnableFeedbackButton() || this.feedbackSubmittedValue;
    }
    if (this.hasNoMoreQuestionsCheckboxTarget) {
      this.noMoreQuestionsCheckboxTarget.disabled =
        this.feedbackSubmittedValue || !this.meetsMinimumMessageCount();
    }
  };

  noMoreQuestionsCheckboxChanged = () => {
    this.updateFeedbackButtonState();
  };

  noMoreQuestionsCheckboxTargetConnected() {
    this.updateFeedbackButtonState();
  }

  debouncedSyncAfterMessagesDomChange = () => {
    const threadId = resolvedThreadId(this);
    const mount = threadId ? document.getElementById(`thread_messages_${threadId}`) : null;
    if (!threadId || !mount || !this.element.contains(mount)) return;
    debounceSync(this, () => {
      void this.syncMessagesToDus().then(ok => {
        if (ok) this.finishTurnIfReady();
      });
    });
  };

  stuckAwaitingAssistantFromMessages(messages) {
    const msgs = Array.isArray(messages) ? messages : [];
    if (!msgs.length) return false;
    const lastMsg = lastMessage(msgs);
    return (
      (isUserMessage(lastMsg) && msgContent(lastMsg).trim())
      || (isAssistantMessage(lastMsg) && !msgContent(lastMsg).trim())
    );
  }

  scheduleConnectStuckTurnRecoveryIfNeeded() {
    if (this.feedbackSubmittedValue || this._connectRecoveryRefetchScheduled) return;
    if (!this.stuckAwaitingAssistantFromMessages(this.messagesValue)) return;
    this._connectRecoveryRefetchScheduled = true;
    this._connectRecoveryTimeoutId = setTimeout(() => {
      this._connectRecoveryTimeoutId = null;
      void this.syncMessagesToDus().then(ok => {
        if (ok) this.finishTurnIfReady();
      });
    }, 2000);
  }

  async syncMessagesToDus() {
    const threadId = resolvedThreadId(this);
    if (!threadId || !this.documentIdValue || !this.userIdValue || !this.nodeUidValue) return false;
    try {
      const res = await fetch(
        `/ai_assistant/messages/sync_from_thread?thread_id=${encodeURIComponent(threadId)}`,
        {
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
        },
      );
      if (!res.ok) return false;
      let data;
      try { data = await res.json(); } catch { return false; }

      this.threadIdValue = data.thread_id;
      this.messagesValue = data.messages;

      const nodeDus = getDusFromDom()?.[normalizeNodeUid(this.nodeUidValue)] || {};
      const existingThreadId = nodeDus?.threadId?.value;
      const existingMessages = nodeDus?.messages?.value;
      const threadChanged = String(existingThreadId || '') !== String(data.thread_id || '');
      const messagesChanged =
        JSON.stringify(existingMessages ?? null) !== JSON.stringify(data.messages ?? null);

      if (threadChanged || messagesChanged) {
        await patchDocumentUserState({
          documentId: this.documentIdValue,
          userId: this.userIdValue,
          body: {
            document_user_state: {
              state: {
                [normalizeNodeUid(this.nodeUidValue)]: {
                  threadId: { value: data.thread_id, updatedAt: Date.now() },
                  messages: { value: data.messages, updatedAt: Date.now() },
                },
              },
            },
          },
        });
      }
      this.messagesValueChanged();
      this.updateSendButtonState();
      if (this._awaitingAssistantResponse && this.stuckAwaitingAssistantFromMessages(this.messagesValue)) {
        this.scheduleResponseSafetyTimeout();
      }
      return true;
    } catch {
      return false;
    }
  }

  finishTurnIfReady() {
    if (this.feedbackSubmittedValue) return;
    const msgs = Array.isArray(this.messagesValue) ? this.messagesValue : [];
    const lastMsg = lastMessage(msgs);
    if (isAssistantError(lastMsg)) {
      const { isConsecutiveError, errorTurnKey } = checkConsecutiveAssistantError(msgs);
      if (
        !vpeAiFallbackCardsAppliedInDus(getDusFromDom())
        && isConsecutiveError
        && this._lastCountedAssistantErrorKey !== errorTurnKey
      ) {
        this._lastCountedAssistantErrorKey = errorTurnKey;
        this.errorCount += 2;
        this.maybeOpenFatalErrorModalIfMaxRetries();
      }
      this.enableInputForNextTurn();
    } else if (isAssistantDone(lastMsg)) {
      this.errorCount = 0;
      this._lastCountedAssistantErrorKey = null;
      this.enableInputForNextTurn();
    }
  }

  maybeOpenFatalErrorModalIfMaxRetries = () => {
    if (this.errorCount < MAX_RETRIES || this.errorModalOpen) return;
    const dus = getDusFromDom();
    if (vpeAiFallbackCardsAppliedInDus(dus)) return;
    const latchKey = vpeFatalErrorModalLatchKey(this.documentIdValue, this.userIdValue);
    if (vpeFatalErrorModalOpenedForDocUser.has(latchKey)) return;
    this.openFatalErrorModalForChatError();
  };

  openFatalErrorModalForChatError() {
    const latchKey = vpeFatalErrorModalLatchKey(this.documentIdValue, this.userIdValue);
    if (
      this.errorModalOpen
      || vpeAiFallbackCardsAppliedInDus(getDusFromDom())
      || vpeFatalErrorModalOpenedForDocUser.has(latchKey)
    ) {
      return;
    }

    this.errorModalOpen = true;
    markVpePersistedChatError(this.documentIdValue, this.userIdValue);
    void this.showErrorModal().then((shown) => {
      if (!shown) this.errorModalOpen = false;
    });
    void this.recordChatErrorAndNotifyFeedback().catch(() => {});
  };

  maybeRestorePersistedFatalErrorModal = async () => {
    if (this._restoringPersistedFatalErrorModal || this._enablingAiFallback) return;

    const dus = getDusFromDom();
    const persistedInDus = dus?.root?.error?.value === 'aiAssistantChatError';
    const persistedInSession = hasVpePersistedChatError({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      dus,
    });
    const modalAlreadyVisible = this.hasErrorModalTarget
      && isRailsuiModalOpen(this.errorModalTarget);
    if (this.errorModalOpen || this.feedbackSubmittedValue) return;
    if (vpeAiFallbackCardsAppliedInDus(dus)) return;
    if (!persistedInDus) {
      if (persistedInSession) {
        clearVpePersistedChatError(this.documentIdValue, this.userIdValue);
      }
      return;
    }
    if (modalAlreadyVisible) {
      this.errorModalOpen = true;
      if (this.hasErrorModalTarget) {
        await waitForEmbeddedVpeErrorModalReady();
        openVpeFallbackErrorModal(this.application, this.errorModalTarget);
      }
      return;
    }

    this._restoringPersistedFatalErrorModal = true;
    try {
      const shown = await this.showErrorModal();
      if (shown) void this.recordChatErrorAndNotifyFeedback().catch(() => {});
    } finally {
      this._restoringPersistedFatalErrorModal = false;
    }
  };

  scheduleResponseSafetyTimeout(source = 'unknown') {
    if (this.feedbackSubmittedValue) return;
    if (!this._responseSafetyDeadline) return;

    const remainingMs = this._responseSafetyDeadline - Date.now();
    clearResponseSafetyTimer(this);
    this._responseSafetyTimeoutSource = source;

    if (remainingMs <= 0) {
      this.handleResponseSafetyTimeout(source);
      return;
    }

    this._responseSafetyTimeout = setTimeout(
      () => this.handleResponseSafetyTimeout(this._responseSafetyTimeoutSource),
      remainingMs,
    );
  }

  handleResponseSafetyTimeout(source = 'unknown') {
    clearResponseSafetyWatch(this);
    if (this.feedbackSubmittedValue || !this._awaitingAssistantResponse) return;

    registerErrorInRollbar('VPE chat response safety timeout', {
      source,
      documentId: this.documentIdValue,
      nodeUid: this.nodeUidValue,
    });
    this.restoreFormAfterError();
    this.openFatalErrorModalForChatError();
  }

  enableInputForNextTurn() {
    clearResponseSafetyWatch(this);
    if (this.feedbackSubmittedValue) return;
    const shouldFocus = this._awaitingAssistantResponse;
    this._awaitingAssistantResponse = false;
    const cacheKey = makeAwaitingCacheKey(this);
    if (cacheKey) {
      awaitingTurnCache.delete(cacheKey);
      pendingFocusCache.delete(cacheKey);
    }
    setInputState(this, false, 'Ask me anything');
    this.updateSendButtonState();
    if (this.hasInputTarget && shouldFocus) {
      this._pendingFocusAfterTurn = true;
      if (cacheKey) pendingFocusCache.set(cacheKey, true);
      requestAnimationFrame(() => {
        if (this.hasInputTarget && this.inputTarget.isConnected && !this.inputTarget.disabled) {
          this.inputTarget.focus();
          this._pendingFocusAfterTurn = false;
          if (cacheKey) pendingFocusCache.delete(cacheKey);
        }
      });
    }
  }

  formSubmitStart = () => {
    if (this.feedbackSubmittedValue) return;
    this._awaitingAssistantResponse = true;
    this._responseSafetyDeadline = Date.now() + RESPONSE_SAFETY_TIMEOUT_MS;
    this.scheduleResponseSafetyTimeout('formSubmitStart');
    this.applyAwaitingAssistantInputUi();
    if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = true;
  };

  formSubmitEnd = e => {
    if (!e.detail.success) {
      this.restoreFormAfterError();
      return;
    }
    void this.syncMessagesToDus().then(ok => {
      if (ok) this.finishTurnIfReady();
    });
  };

  formFetchError = () => {
    clearResponseSafetyWatch(this);
    this.restoreFormAfterError();
    this.errorCount += 1;
    this.maybeOpenFatalErrorModalIfMaxRetries();
  };

  restoreFormAfterError() {
    clearResponseSafetyWatch(this);
    if (this.feedbackSubmittedValue) return;
    this._awaitingAssistantResponse = false;
    const cacheKey = makeAwaitingCacheKey(this);
    if (cacheKey) {
      awaitingTurnCache.delete(cacheKey);
      pendingFocusCache.delete(cacheKey);
    }
    setInputState(this, false, 'Ask me anything');
    this.updateSendButtonState();
  }

  recordChatErrorAndNotifyFeedback = async () => {
    const dus = getDusFromDom();
    if (vpeAiFallbackCardsAppliedInDus(dus)) return;
    if (dus?.root?.error?.value !== 'aiAssistantChatError') {
      const now = Date.now();
      updateDusInDom({
        ...dus,
        root: {
          ...(dus.root && typeof dus.root === 'object' ? dus.root : {}),
          error: { value: 'aiAssistantChatError', updatedAt: now },
        },
      });
      markVpePersistedChatError(this.documentIdValue, this.userIdValue);
      await patchDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              root: {
                error: { value: 'aiAssistantChatError', updatedAt: now },
              },
            },
          },
        },
      });
    }
    document.dispatchEvent(new CustomEvent('vpe-chat-error', { bubbles: true }));
  };

  showErrorModal = async () => {
    const latchKey = vpeFatalErrorModalLatchKey(this.documentIdValue, this.userIdValue);
    const fallbackModalRoot = this.hasErrorModalTarget ? this.errorModalTarget : null;
    const openEmbeddedModal = async () => openVpeFallbackErrorModal(this.application, fallbackModalRoot);
    if (fallbackModalRoot?.isConnected) {
      await waitForEmbeddedVpeErrorModalReady();

      if (await openEmbeddedModal()) {
        this.errorModalOpen = true;
        vpeFatalErrorModalOpenedForDocUser.add(latchKey);
        return true;
      }
    }
    const shown = await showAiErrorModal({
      documentId: this.documentIdValue,
      application: this.application,
      buttonId: 'vpe-chat-error-continue-button',
      fallbackModalRoot,
    });
    if (shown) {
      this.errorModalOpen = true;
      vpeFatalErrorModalOpenedForDocUser.add(latchKey);
    }
    return shown;
  };

  continueFromErrorModal = async (event) => {
    event?.preventDefault?.();
    const result = await this.enableAiFallbackMode();
    if (result === false) this._reenableErrorModalContinueButtons();
    return result;
  };

  _reenableErrorModalContinueButtons() {
    const modalRoots = [
      this.hasErrorModalTarget ? this.errorModalTarget : null,
      ...document.querySelectorAll('[data-dynamic-vpe-error-modal]'),
    ].filter(Boolean);

    modalRoots.forEach((modalRoot) => {
      vpeErrorModalController(this.application, modalRoot)?.reenableContinue();
    });
  }

  hideErrorModal = () => {
    if (this.hasErrorModalTarget) {
      closeRailsuiModal(this.application, this.errorModalTarget);
    }
    removeDynamicVpeErrorModals(this.application);
    this.errorModalOpen = false;
    this.errorCount = 0;
    this._lastCountedAssistantErrorKey = null;
    vpeFatalErrorModalOpenedForDocUser.delete(
      vpeFatalErrorModalLatchKey(this.documentIdValue, this.userIdValue),
    );
  };

  async enableAiFallbackMode() {
    if (this._enablingAiFallback) return false;
    this._enablingAiFallback = true;
    clearResponseSafetyWatch(this);
    this._awaitingAssistantResponse = false;
    const now = Date.now();
    const body = {
      document_user_state: {
        state: {
          root: {
            error: { value: 'aiAssistantChatError', updatedAt: now },
            aiFallbackCards: { value: true, updatedAt: now },
          },
        },
      },
    };
    const response = await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body,
    });
    try {
      if (!response) {
        registerErrorInRollbar('VPE chat error modal: fallback DUS patch failed', {
          documentId: this.documentIdValue,
          userId: this.userIdValue,
          nodeUid: this.nodeUidValue,
        });
        this._reenableErrorModalContinueButtons();
        return false;
      }

      await handleAiAssistantFallback({
        currentSectionElement: currentSection(this.element),
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        application: this.application,
      });
      this.hideErrorModal();
      return true;
    } finally {
      this._enablingAiFallback = false;
    }
  }

  openFeedbackModal = event => {
    event?.preventDefault?.();
    if (!this.hasFeedbackModalTarget || this.feedbackSubmittedValue) return;
    const threadId = resolvedThreadId(this);
    if (!threadId) {
      registerErrorInRollbar('VPE feedback opened without thread id', { nodeUid: this.nodeUidValue });
      return;
    }
    this.element.dispatchEvent(new CustomEvent('vpe-chat-interview-ending'));
    this._ensureVpeFeedbackModalListeners();
    if (this.hasFeedbackModalHostTarget) this.feedbackModalHostTarget.style.display = '';
    document.body.style.overflow = 'hidden';
    this.feedbackModalTarget.src = this.buildVpeFeedbackModalUrl(threadId, 'vpe_chat');
  };

  continueToNextSection = async () => {
    await continueToNextSection({
      element: this.element,
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      application: this.application,
      createContinueButtonIfNeeded: false,
    });
  };
}
